import React from 'react';
import { Time } from './types';

interface SetMinutesSliderProps {
  setTime: (fn: any) => void;
  time: Time;
}

const SetMinutesSlider = ({ setTime, time }: SetMinutesSliderProps) => {
  const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentValueRef = React.useRef(time.initialMinutes);
  const [inputValue, setInputValue] = React.useState(String(time.initialMinutes));
  const ignoreMouseEvents = React.useRef(false);

  React.useEffect(() => {
    const rounded = Math.max(5, Math.round(time.initialMinutes / 5) * 5);
    currentValueRef.current = rounded;
    setInputValue(String(rounded));
  }, [time.initialMinutes]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valueString = e.target.value;
    setInputValue(valueString);
    if (valueString === '') return;
    const parsed = parseInt(valueString);
    if (isNaN(parsed)) return;
    const snapped = Math.round(parsed / 5) * 5;
    const value = Math.max(5, Math.min(120, snapped));
    setTime({ ...time, initialMinutes: value, secondsRemaining: value * 60 });
  };

  const updateValue = (delta: number) => {
    const newValue = Math.max(5, Math.min(120, currentValueRef.current + delta));
    currentValueRef.current = newValue;
    setTime({ ...time, initialMinutes: newValue, secondsRemaining: newValue * 60 });
  };

  const startHold = (delta: number, event?: React.TouchEvent | React.MouseEvent) => {
    const isTouch = event && 'touches' in event;
    if (isTouch) {
      ignoreMouseEvents.current = true;
      setTimeout(() => { ignoreMouseEvents.current = false; }, 700);
    }
    if (!isTouch && ignoreMouseEvents.current) return;
    if (timeoutRef.current || intervalRef.current) return;
    updateValue(delta);
    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(() => updateValue(delta), 100);
    }, 1000);
  };

  const stopHold = () => {
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
  };

  React.useEffect(() => () => stopHold(), []);

  return (
    <div className="bg-parchment-100 rounded-xl shadow-sm border border-parchment-200 p-6 text-center w-80 mb-4">
      <p className="text-sm font-semibold text-ink mb-4">Check in after</p>

      <div className="flex items-center justify-center gap-4 mb-4">
        <button
          className="w-12 h-12 rounded-full border-2 border-leather text-leather text-xl font-bold hover:bg-parchment-200 transition select-none"
          onMouseDown={(e) => startHold(-5, e)}
          onMouseUp={stopHold}
          onMouseLeave={stopHold}
          onTouchStart={(e) => startHold(-5, e)}
          onTouchEnd={stopHold}
        >
          −
        </button>

        <input
          type="number"
          value={inputValue}
          onChange={handleChange}
          min={1}
          max={120}
          className="w-24 text-center text-5xl font-bold text-ink border-none outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />

        <button
          className="w-12 h-12 rounded-full border-2 border-leather text-leather text-xl font-bold hover:bg-parchment-200 transition select-none"
          onMouseDown={(e) => startHold(5, e)}
          onMouseUp={stopHold}
          onMouseLeave={stopHold}
          onTouchStart={(e) => startHold(5, e)}
          onTouchEnd={stopHold}
        >
          +
        </button>
      </div>

      <p className="text-sm font-semibold text-ink mb-1">minutes</p>
      <p className="text-xs text-ink-muted">Read as long as you need. We'll check in when time's up.</p>
    </div>
  );
};

export default SetMinutesSlider;
